import { LicenseManager } from 'ag-grid-enterprise';
import axios from 'axios';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ProvideAuth } from '../scripts/fb';
import { App } from './app';
import './globals.css';
import { Userpilot } from 'userpilot';
import ReactGA from 'react-ga';
import * as Sentry from '@sentry/react';
import { ErrorPage } from './common/components/ErrorBoundary';
// Initialize Sentry
Sentry.init({
  dsn: process.env.VITE_SENTRY_DSN || '',
  integrations: [
    // Initialize browser tracing integration
    {
      name: 'browser-tracing',
      setupOnce: () => {}
    }
  ],
  // Performance Monitoring
  tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.2 : 1.0,
  // Set environment
  environment: process.env.NODE_ENV || 'development'
});

// Initialize Userpilot
Userpilot.initialize('NX-d849c69f');

LicenseManager.setLicenseKey(
  'CompanyName=Drumroll Health Inc.,LicensedApplication=Drumroll' +
    ',LicenseType=SingleApplication,LicensedConcurrentDeveloperCount=1' +
    ',LicensedProductionInstancesCount=1,AssetReference=AG-035551' +
    ',SupportServicesEnd=6_December_2023_[v2]_MTcwMTgyMDgwMDAwMA==' +
    '944c1e128cfedb7fcd5a503186639035'
);

const trackingId = 'AW-16569301093';
ReactGA.initialize(trackingId, {
  gaOptions: { siteSpeedSampleRate: 100 }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Aggressive deduplication and caching
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Don't refetch on component mount
      refetchOnReconnect: false,
      retry: false
    }
  }
});
axios.defaults.baseURL = process.env.VITE_API;

// Store request metadata outside axios config to avoid TypeScript issues
const requestMetadataMap = new Map<string, { startTime: number; requestId: string }>();

// Global axios interceptor to track all /crosstab requests
axios.interceptors.request.use(
  (config) => {
    if (config.url?.includes('/crosstab')) {
      const requestId = `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Store metadata in Map using unique key
      const metadataKey = `${config.method}-${config.url}-${requestId}`;
      requestMetadataMap.set(metadataKey, {
        startTime: performance.now(),
        requestId
      });

      // Add request ID to headers for tracking
      if (!config.headers) {
        config.headers = {} as any;
      }
      config.headers['X-Request-ID'] = requestId;
      config.headers['X-Metadata-Key'] = metadataKey;

      // Extract view info from request data
      let viewInfo = 'no-view';
      try {
        if (config.data) {
          const data = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
          viewInfo = data?.fields?.[0] || data?.view || 'unknown-view';
        }
      } catch (e) {
        viewInfo = 'parse-error';
      }

      // Log request details with stack trace to identify source
      console.log(
        `%c[AXIOS REQUEST] ${config.method?.toUpperCase()} ${config.url}`,
        'color: #FF9800; font-weight: bold; font-size: 12px;',
        {
          requestId,
          method: config.method,
          url: config.url,
          view: viewInfo,
          timestamp: new Date().toLocaleTimeString(),
          callStack: new Error().stack?.split('\n').slice(2, 8).join('\n')
        }
      );
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => {
    if (response.config.url?.includes('/crosstab')) {
      const metadataKey = response.config.headers?.['X-Metadata-Key'] as string;
      const metadata = metadataKey ? requestMetadataMap.get(metadataKey) : null;

      const duration = metadata?.startTime ? Math.round(performance.now() - metadata.startTime) : 0;
      const requestId =
        metadata?.requestId || response.config.headers?.['X-Request-ID'] || 'unknown';

      console.log(
        `%c[AXIOS RESPONSE] ${response.config.method?.toUpperCase()} ${response.config.url}`,
        'color: #4CAF50; font-weight: bold; font-size: 12px;',
        {
          requestId,
          status: response.status,
          records: response.data?.data?.length || 0,
          duration: `${duration}ms`,
          timestamp: new Date().toLocaleTimeString()
        }
      );

      // Clean up metadata
      if (metadataKey) {
        requestMetadataMap.delete(metadataKey);
      }
    }
    return response;
  },
  (error) => {
    if (error.config?.url?.includes('/crosstab')) {
      const metadataKey = error.config.headers?.['X-Metadata-Key'] as string;
      const metadata = metadataKey ? requestMetadataMap.get(metadataKey) : null;

      const duration = metadata?.startTime ? Math.round(performance.now() - metadata.startTime) : 0;
      const requestId = metadata?.requestId || error.config.headers?.['X-Request-ID'] || 'unknown';

      console.error(
        `%c[AXIOS ERROR] ${error.config.method?.toUpperCase()} ${error.config.url}`,
        'color: #F44336; font-weight: bold; font-size: 12px;',
        {
          requestId,
          status: error.response?.status || 'network-error',
          message: error.message,
          duration: `${duration}ms`,
          timestamp: new Date().toLocaleTimeString()
        }
      );

      // Clean up metadata
      if (metadataKey) {
        requestMetadataMap.delete(metadataKey);
      }
    }
    return Promise.reject(error);
  }
);

const root: HTMLElement = document.getElementById('root')!;

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <ErrorPage>
      <QueryClientProvider client={queryClient}>
        <ProvideAuth>
          <App />
        </ProvideAuth>
      </QueryClientProvider>
    </ErrorPage>
  </React.StrictMode>
);
